import React, { useState, useMemo } from 'react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  BarChart3,
  TreePine,
  Users,
  Calendar,
  CheckCircle2,
  MapPin,
  Edit,
  Clock,
  Package,
  Map,
  Settings,
  ChevronLeft,
  ChevronRight,
  Wrench,
  Database,
  ClipboardList,
  Globe,
  MessageCircle,
  CalendarClock,
  LogIn,
  UserCheck,
  FileSignature,
  Radio,
  ClipboardCheck
} from "lucide-react";
import { useAuthorization } from "@/auth/AuthorizationContext";
import { OBT_ALLOWED_ROLES } from "@/components/obt/obtRoles";


// ============================================================================
// SPRINT 2 F5b: Dinamik Permission Tabanlı Menü
// ============================================================================
// Eski sistemde menu item'ları rol bazlı statik liste (menuItems[userRole])
// idi. Yeni sistemde: tek master liste + her item'da `requires` (gerekli
// permission slug'ları). Kullanıcı, en az BİR slug'a sahipse item görünür.
//
// `requires: []` (boş) = herkese görünür (örn. Ana Sayfa, Profilim).
// `requires: ['izin.gor']` = sadece bu yetkiye sahipse.
// `requires: ['yonetim.rol_yonet', 'yonetim.yetki_yonet']` = hasAny mantığı.
//
// Admin panelden yeni bir role bir yetki atandığında, o role sahip kullanıcı
// login olduğunda ilgili menü item'ları OTOMATİK görünür hâle gelir.
//
// OBT EK KURALI (`roles`): Bazı modüllerin backend tarafında permission slug'ı
// yoktur; yetkilendirme doğrudan rol + hiyerarşik zincir üzerinden yapılır
// (bkz. backend/modules/obt/scope.py). Böyle bir item'a `roles: [...]` verilir
// ve item YALNIZ bu rollere gösterilir. `roles` verilmeyen item'lar eskisi gibi
// davranır; mevcut menü öğelerinin davranışı değişmez.
// ============================================================================

const MASTER_MENU = [
  { id: 'dashboard',           label: 'Ana Sayfa',                icon: BarChart3,    requires: [] },
  { id: 'operations',          label: 'Anlık Görevlendirme',      icon: Radio,        requires: ['operasyon.gor'] },
  { id: 'obt',                 label: 'Operasyonel Bilgi Toplama', icon: ClipboardCheck, requires: [], roles: OBT_ALLOWED_ROLES },
  { id: 'parks',               label: 'Parklar',                  icon: TreePine,     requires: ['park.gor', 'park.listele'] },
  { id: 'daily-maintenance',   label: 'Park Bakım Yönetimi',      icon: CheckCircle2, requires: ['bakim.gor'] },
  { id: 'task-tracking',       label: 'Görev Takip',              icon: ClipboardList, requires: ['personel.listele', 'personel.tum_kapsam'] },
  { id: 'location-tracking',   label: 'Görev Alanı Yönetimi',     icon: MapPin,       requires: ['lokasyon.gor', 'lokasyon.tum_kapsam'] },
  { id: 'personnel',           label: 'Personel',                 icon: Users,        requires: ['personel.listele'] },
  { id: 'passive-areas',       label: 'Pasif Alanlar',            icon: Map,          requires: ['pasif_alan.gor', 'pasif_alan.listele'] },
  { id: 'protokol-parks',      label: 'Protokollü Parklar',        icon: FileSignature, requires: ['protokol_park.gor', 'protokol_park.listele'] },
  { id: 'regions',             label: 'Bölgeler',                 icon: Globe,        requires: ['park.listele', 'personel.tum_kapsam'] },
  { id: 'activity-management', label: 'Faaliyet Yönetimi',        icon: Calendar,     requires: ['faaliyet.gor', 'faaliyet.olustur', 'faaliyet.duzenle'] },
  { id: 'approvals',           label: 'İzin Yönetimi',            icon: Clock,        requires: ['izin.gor', 'izin.listele', 'izin.olustur'] },
  { id: 'revisions',           label: 'Revize Yönetimi',          icon: Edit,         requires: ['revizyon.gor', 'revizyon.olustur'] },
  { id: 'materials',           label: 'Malzeme Yönetimi',         icon: Package,      requires: ['malzeme.gor'] },
  // Aşama C: Rol Yönetimi ve Veri Yönetimi sekmeleri yalnızca Yönetici Paneli'nde (/admin).
  // Kullanıcı panelinden tamamen kaldırıldı.
  { id: 'profile',             label: 'Profilim',                 icon: UserCheck,    requires: [] },
  { id: 'messages',            label: 'Mesajlar',                 icon: MessageCircle, requires: ['bildirim.gor'] },
  { id: 'overtime-management', label: 'Mesai Yönetimi',           icon: CalendarClock, requires: ['mesai.gor', 'mesai.olustur'] },
  { id: 'audit-logs',          label: 'İşlem Geçmişi',            icon: ClipboardList, requires: ['yonetim.audit_log', 'audit.gor'] },
  { id: 'login-management',    label: 'Sisteme Giriş Yönetimi',   icon: LogIn,        requires: ['personel.login_yonet'] },
];


// LEGACY menuItems — yedek olarak korunur (AuthorizationContext yüklenmediyse
// veya hiç permission yoksa fallback). Login öncesi/sırasında bu kullanılır.
const menuItems = {
  mudur: [
    { id: 'dashboard', label: 'Ana Sayfa', icon: BarChart3 },
    { id: 'obt', label: 'Operasyonel Bilgi Toplama', icon: ClipboardCheck },
    { id: 'parks', label: 'Parklar', icon: TreePine },
    { id: 'daily-maintenance', label: 'Park Bakım Yönetimi', icon: CheckCircle2 },
    { id: 'task-tracking', label: 'Görev Takip', icon: ClipboardList },
    { id: 'location-tracking', label: 'Görev Alanı Yönetimi', icon: MapPin },
    { id: 'personnel', label: 'Personel', icon: Users },
    { id: 'passive-areas', label: 'Pasif Alanlar', icon: Map },
    { id: 'protokol-parks', label: 'Protokollü Parklar', icon: FileSignature },
    { id: 'regions', label: 'Bölgeler', icon: Globe },
    { id: 'activity-management', label: 'Faaliyet Yönetimi', icon: Calendar },
    { id: 'approvals', label: 'İzin Yönetimi', icon: Clock },
    { id: 'revisions', label: 'Revize Yönetimi', icon: Edit },
    { id: 'materials', label: 'Malzeme Yönetimi', icon: Package },
    // Aşama C: Rol Yönetimi ve Veri Yönetimi sekmeleri yalnızca Yönetici Paneli'nde (/admin).
    { id: 'profile', label: 'Profilim', icon: UserCheck },
    { id: 'messages', label: 'Mesajlar', icon: MessageCircle },
    { id: 'overtime-management', label: 'Mesai Yönetimi', icon: CalendarClock },
    { id: 'audit-logs', label: 'İşlem Geçmişi', icon: ClipboardList },
    { id: 'login-management', label: 'Sisteme Giriş Yönetimi', icon: LogIn },
  ],
  admin: [
    { id: 'dashboard', label: 'Ana Sayfa', icon: BarChart3 },
    { id: 'parks', label: 'Parklar', icon: TreePine },
    { id: 'daily-maintenance', label: 'Park Bakım Yönetimi', icon: CheckCircle2 },
    { id: 'task-tracking', label: 'Görev Takip', icon: ClipboardList },
    { id: 'location-tracking', label: 'Görev Alanı Yönetimi', icon: MapPin },
    { id: 'personnel', label: 'Personel', icon: Users },
    { id: 'passive-areas', label: 'Pasif Alanlar', icon: Map },
    { id: 'protokol-parks', label: 'Protokollü Parklar', icon: FileSignature },
    { id: 'regions', label: 'Bölgeler', icon: Globe },
    { id: 'activity-management', label: 'Faaliyet Yönetimi', icon: Calendar },
    { id: 'approvals', label: 'İzin Yönetimi', icon: Clock },
    { id: 'revisions', label: 'Revize Yönetimi', icon: Edit },
    { id: 'materials', label: 'Malzeme Yönetimi', icon: Package },
    // Aşama C: Rol Yönetimi ve Veri Yönetimi sekmeleri yalnızca Yönetici Paneli'nde (/admin).
    { id: 'profile', label: 'Profilim', icon: UserCheck },
    { id: 'messages', label: 'Mesajlar', icon: MessageCircle },
    { id: 'overtime-management', label: 'Mesai Yönetimi', icon: CalendarClock },
    { id: 'audit-logs', label: 'İşlem Geçmişi', icon: ClipboardList },
    { id: 'login-management', label: 'Sisteme Giriş Yönetimi', icon: LogIn },
  ],
  mudur_yardimcisi: [
    { id: 'dashboard', label: 'Ana Sayfa', icon: BarChart3 },
    { id: 'obt', label: 'Operasyonel Bilgi Toplama', icon: ClipboardCheck },
    { id: 'parks', label: 'Parklar', icon: TreePine },
    { id: 'daily-maintenance', label: 'Park Bakım Yönetimi', icon: CheckCircle2 },
    { id: 'task-tracking', label: 'Görev Takip', icon: ClipboardList },
    { id: 'location-tracking', label: 'Görev Alanı Yönetimi', icon: MapPin },
    { id: 'personnel', label: 'Personel', icon: Users },
    { id: 'passive-areas', label: 'Pasif Alanlar', icon: Map },
    { id: 'protokol-parks', label: 'Protokollü Parklar', icon: FileSignature },
    { id: 'regions', label: 'Bölgeler', icon: Globe },
    { id: 'activity-management', label: 'Faaliyet Yönetimi', icon: Calendar },
    { id: 'approvals', label: 'İzin Yönetimi', icon: Clock },
    { id: 'revisions', label: 'Revize Yönetimi', icon: Edit },
    { id: 'materials', label: 'Malzeme Yönetimi', icon: Package },
    { id: 'profile', label: 'Profilim', icon: UserCheck },
    { id: 'messages', label: 'Mesajlar', icon: MessageCircle },
    { id: 'overtime-management', label: 'Mesai Yönetimi', icon: CalendarClock },
  ],
  bolge_muhendisi: [
    { id: 'dashboard', label: 'Ana Sayfa', icon: BarChart3 },
    { id: 'obt', label: 'Operasyonel Bilgi Toplama', icon: ClipboardCheck },
    { id: 'parks', label: 'Parklar', icon: TreePine },
    { id: 'daily-maintenance', label: 'Park Bakım Yönetimi', icon: CheckCircle2 },
    { id: 'task-tracking', label: 'Görev Takip', icon: ClipboardList },
    { id: 'location-tracking', label: 'Görev Alanı Yönetimi', icon: MapPin },
    { id: 'personnel', label: 'Personel', icon: Users },
    { id: 'passive-areas', label: 'Pasif Alanlar', icon: Map },
    { id: 'protokol-parks', label: 'Protokollü Parklar', icon: FileSignature },
    { id: 'regions', label: 'Bölgeler', icon: Globe },
    { id: 'activity-management', label: 'Faaliyet Yönetimi', icon: Calendar },
    { id: 'approvals', label: 'İzin Yönetimi', icon: Clock },
    { id: 'revisions', label: 'Revize Yönetimi', icon: Edit },
    { id: 'materials', label: 'Malzeme Yönetimi', icon: Package },
    { id: 'profile', label: 'Profilim', icon: UserCheck },
    { id: 'messages', label: 'Mesajlar', icon: MessageCircle },
    { id: 'overtime-management', label: 'Mesai Yönetimi', icon: CalendarClock },
  ],
  formen: [
    { id: 'dashboard', label: 'Ana Sayfa', icon: BarChart3 },
    { id: 'obt', label: 'Operasyonel Bilgi Toplama', icon: ClipboardCheck },
    { id: 'parks', label: 'Parklar', icon: TreePine },
    { id: 'daily-maintenance', label: 'Park Bakım Yönetimi', icon: CheckCircle2 },
    { id: 'task-tracking', label: 'Görev Takip', icon: ClipboardList },
    { id: 'location-tracking', label: 'Görev Alanı Yönetimi', icon: MapPin },
    { id: 'personnel', label: 'Personel', icon: Users },
    { id: 'passive-areas', label: 'Pasif Alanlar', icon: Map },
    { id: 'protokol-parks', label: 'Protokollü Parklar', icon: FileSignature },
    { id: 'regions', label: 'Bölgeler', icon: Globe },
    { id: 'activity-management', label: 'Faaliyet Yönetimi', icon: Calendar },
    { id: 'approvals', label: 'İzin Yönetimi', icon: Clock },
    { id: 'revisions', label: 'Revize Yönetimi', icon: Edit },
    { id: 'materials', label: 'Malzeme Yönetimi', icon: Package },
    { id: 'profile', label: 'Profilim', icon: UserCheck },
    { id: 'messages', label: 'Mesajlar', icon: MessageCircle },
    { id: 'overtime-management', label: 'Mesai Yönetimi', icon: CalendarClock },
  ],
  park_gorevlisi: [
    { id: 'dashboard', label: 'Ana Sayfa', icon: BarChart3 },
    { id: 'daily-maintenance', label: 'Görevlerim', icon: CheckCircle2 },
    { id: 'revisions', label: 'Revize Yönetimi', icon: Edit },
    { id: 'approvals', label: 'İzin Yönetimi', icon: Clock },
    { id: 'materials', label: 'Malzeme Yönetimi', icon: Package },
    { id: 'profile', label: 'Profilim', icon: UserCheck },
    { id: 'messages', label: 'Mesajlar', icon: MessageCircle },
    { id: 'overtime-management', label: 'Mesai Yönetimi', icon: CalendarClock },
  ],
  cim_ekibi: [
    { id: 'daily-maintenance', label: 'Görevlerim', icon: CheckCircle2 },
    { id: 'approvals', label: 'İzin Yönetimi', icon: Clock },
    { id: 'profile', label: 'Profilim', icon: UserCheck },
    { id: 'messages', label: 'Mesajlar', icon: MessageCircle },
    { id: 'overtime-management', label: 'Mesai Yönetimi', icon: CalendarClock },
  ],
  golge_admin: [
    { id: 'dashboard', label: 'Ana Sayfa', icon: BarChart3 },
    { id: 'parks', label: 'Parklar', icon: TreePine },
    { id: 'daily-maintenance', label: 'Park Bakım Yönetimi', icon: CheckCircle2 },
    { id: 'task-tracking', label: 'Görev Takip', icon: ClipboardList },
    { id: 'location-tracking', label: 'Görev Alanı Yönetimi', icon: MapPin },
    { id: 'personnel', label: 'Personel', icon: Users },
    { id: 'passive-areas', label: 'Pasif Alanlar', icon: Map },
    { id: 'protokol-parks', label: 'Protokollü Parklar', icon: FileSignature },
    { id: 'regions', label: 'Bölgeler', icon: Globe },
    { id: 'activity-management', label: 'Faaliyet Yönetimi', icon: Calendar },
    { id: 'approvals', label: 'İzin Yönetimi', icon: Clock },
    { id: 'revisions', label: 'Revize Yönetimi', icon: Edit },
    { id: 'materials', label: 'Malzeme Yönetimi', icon: Package },
    // Aşama C: Rol Yönetimi ve Veri Yönetimi sekmeleri yalnızca Yönetici Paneli'nde (/admin).
    { id: 'profile', label: 'Profilim', icon: UserCheck },
    { id: 'messages', label: 'Mesajlar', icon: MessageCircle },
    { id: 'overtime-management', label: 'Mesai Yönetimi', icon: CalendarClock },
  ]
};

export default function Sidebar({ userRole, activeTab, onTabChange, badges = {} }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { hasAny, permissions, role: authzRole, loading: authzLoading } = useAuthorization();

  // Rol, doğrulanmış authorization kaynağından okunur; prop yalnız yedektir.
  const effectiveRole = authzRole || userRole || '';

  // Sprint 2 F5b: Dinamik permission tabanlı menü filtresi.
  // - Permissions yüklü ve hasAny calisirsa: MASTER_MENU'den filtre.
  // - Yüklenmediyse veya hiç permission yoksa: legacy rol bazlı liste (fallback).
  const items = useMemo(() => {
    // Permission'lar varsa dinamik filtre
    if (Array.isArray(permissions) && permissions.length > 0) {
      return MASTER_MENU.filter((item) => {
        // Rol kapısı (varsa) HER ZAMAN önce uygulanır; fail-closed.
        if (Array.isArray(item.roles) && !item.roles.includes(effectiveRole)) {
          return false;
        }
        return !item.requires || item.requires.length === 0 || hasAny(item.requires);
      });
    }
    // Fallback: legacy rol bazlı liste (login öncesi veya permission fetch hatası)
    return menuItems[effectiveRole] || menuItems.park_gorevlisi;
  }, [permissions, hasAny, effectiveRole]);
  
  // Badge mapping: menu id -> badge count
  // Badge format:
  // - number
  // - { count: number, muted: boolean }
  // - { primary: { count, muted }, secondary: { count, muted } }
  const getBadgeInfo = (itemId) => {
    const badge = badges[itemId];
    if (!badge) return { primary: { count: 0, muted: false }, secondary: null };
    if (typeof badge === 'number') return { primary: { count: badge, muted: false }, secondary: null };
    if (badge.primary || badge.secondary) {
      return {
        primary: { count: badge.primary?.count || 0, muted: badge.primary?.muted || false },
        secondary: badge.secondary ? { count: badge.secondary.count || 0, muted: badge.secondary.muted || false } : null
      };
    }
    return { primary: { count: badge.count || 0, muted: badge.muted || false }, secondary: null };
  };
  
  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          "bg-white border-r border-gray-200 flex flex-col transition-all duration-300 shadow-sm",
          isCollapsed ? "w-16" : "w-64"
        )}
        style={{ height: "calc(100vh - var(--app-header-h))" }}
      >
        {/* Menu Items */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {(() => {
          const renderItem = (item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const isOps = item.id === 'operations'; // Anlık Görevlendirme — ayırt edici stil
            const badgeInfo = getBadgeInfo(item.id);
            const badgeCount = badgeInfo.primary.count;
            const isMuted = badgeInfo.primary.muted;
            const secondaryBadgeCount = badgeInfo.secondary?.count || 0;
            const secondaryMuted = badgeInfo.secondary?.muted || false;
            
            if (isCollapsed) {
              return (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => onTabChange(item.id)}
                      className={cn(
                        "w-full flex items-center justify-center p-3 rounded-lg transition-all duration-200 relative",
                        isActive
                          ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md"
                          : isOps
                            ? "bg-orange-50 text-orange-600 ring-1 ring-orange-300 hover:bg-orange-100"
                            : "text-gray-600 hover:bg-gray-100 hover:text-green-600"
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      {badgeCount > 0 && (
                        <span className={cn(
                          "absolute -top-1 -right-1 text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold",
                          isMuted 
                            ? "bg-gray-400 text-white" 
                            : "bg-red-500 text-white animate-pulse"
                        )}>
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </span>
                      )}
                      {secondaryBadgeCount > 0 && (
                        <span className={cn(
                          "absolute -top-1 -right-6 text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold",
                          secondaryMuted 
                            ? "bg-gray-400 text-white" 
                            : "bg-red-500 text-white animate-pulse"
                        )}>
                          {secondaryBadgeCount > 99 ? '99+' : secondaryBadgeCount}
                        </span>
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="font-medium">
                    {item.label} {badgeCount > 0 && `(${badgeCount}${secondaryBadgeCount > 0 ? ` + ${secondaryBadgeCount}` : ''})`}
                  </TooltipContent>
                </Tooltip>
              );
            }
            
            return (
              <button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-left relative",
                  isActive
                    ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md"
                    : isOps
                      ? "bg-orange-50 text-orange-700 font-bold ring-1 ring-orange-200 hover:bg-orange-100 pl-4"
                      : "text-gray-600 hover:bg-gray-100 hover:text-green-600"
                )}
              >
                {isOps && !isActive && (
                  <span className="absolute left-0 inset-y-1.5 w-1.5 rounded-r-full bg-orange-500" />
                )}
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className="font-medium text-sm truncate flex-1">{item.label}</span>
                {badgeCount > 0 && (
                  <span className={cn(
                    "ml-auto text-xs rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center font-bold",
                    isActive 
                      ? "bg-white text-green-600" 
                      : isMuted
                        ? "bg-gray-400 text-white"
                        : "bg-red-500 text-white animate-pulse"
                  )}>
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
                {secondaryBadgeCount > 0 && (
                  <span className={cn(
                    "ml-1 text-xs rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center font-bold",
                    isActive 
                      ? "bg-white text-green-600" 
                      : secondaryMuted
                        ? "bg-gray-400 text-white"
                        : "bg-red-500 text-white animate-pulse"
                  )}>
                    {secondaryBadgeCount > 99 ? '99+' : secondaryBadgeCount}
                  </span>
                )}
              </button>
            );
          };
          const opsItem = items.find((i) => i.id === 'operations');
          const restItems = items.filter((i) => i.id !== 'operations');
          return (
            <>
              {opsItem && (
                <div className="mb-1">
                  {!isCollapsed && (
                    <p className="px-3 pt-1 pb-1 text-[10px] font-bold tracking-wider text-orange-500 uppercase">
                      Hızlı Erişim
                    </p>
                  )}
                  {renderItem(opsItem)}
                  <div className="mt-2 mb-1.5 h-0.5 rounded-full bg-gray-300" />
                </div>
              )}
              {restItems.map(renderItem)}
            </>
          );
          })()}
        </nav>
        
        {/* Collapse Toggle */}
        <div className="p-2 border-t border-gray-200">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={cn(
              "w-full flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700",
              !isCollapsed && "justify-start px-3"
            )}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" />
                <span className="text-xs">Daralt</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </TooltipProvider>
  );
}
